
import { useState, memo } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import daniloHeadshot from '@/assets/quantmake-danilo-leandro-especialista-robo-trader.jpg';
import { useScrollAnimation } from '@/hooks/useScrollAnimation';

const TeachersSection = memo(() => {
  const [selectedTeacher, setSelectedTeacher] = useState(0);
  const { ref: headerRef, isVisible: headerVisible } = useScrollAnimation({ threshold: 0.1, rootMargin: '-10% 0px' });

  const teachers = [
    {
      id: 1,
      name: "Danilo Leandro",
      specialty: "Trading Automatizado & Robôs",
      experience: "18+ anos",
      image: "/lovable-uploads/quantmake-danilo-leandro-especialista-robo-trader-engenheiro.png",
      bio: "Danilo Leandro é engenheiro, opera no mercado financeiro desde 2007 e é especialista em programação de estratégias automatizadas para bolsa de valores desde 2015. Ao longo de sua carreira, atuou como assessor de investimentos e desenvolveu Robôs para grandes investidores e para os mais renomados analistas do mercado que já faturaram mais de 7 dígitos.",
      achievements: ["18+ anos no mercado financeiro", "Especialista em programação desde 2015", "Desenvolveu robôs para grandes investidores", "Mentor em trading automatizado", "Parceiro oficial da maior plataforma de trading do Brasil"],
      focus: "Especialista em robôs para o mercado financeiro, mentor em trading automatizado e desenvolvedor de estratégias lucrativas com automação."
    },
    {
      id: 2,
      name: "Andréa Freitas",
      specialty: "Day Trader & Analista CNPI-T",
      experience: "6+ anos",
      image: "/lovable-uploads/quantmake-andrea-melo-analista-cnpi-day-trade-especialista.png",
      bio: "Andréa de Melo Freitas Pessoa é formada em Administração e analista CNPI-T, iniciou sua jornada no mercado financeiro em 2019 com foco em operações no índice e no dólar. Transformou sua paixão pelo mercado em profissão, especializando-se em estratégias de Day Trade e hoje atua com estratégias automatizadas e robôs traders, ajudando outros traders a migrarem para sistemas mais consistentes e estruturados.",
      achievements: ["Analista CNPI-T certificada", "Especialista em Day Trade no índice e dólar", "Mentora de trading com uso de estratégias automatizadas", "Formação de traders em análise técnica"],
      focus: "Especialista em estratégias automatizadas e robôs traders para o mercado futuro, ajuda traders a evoluírem para sistemas automatizados com foco em consistência e resultados."
    }
  ];

  const nextTeacher = () => {
    setSelectedTeacher((prev) => (prev + 1) % teachers.length);
  };

  const prevTeacher = () => {
    setSelectedTeacher((prev) => (prev - 1 + teachers.length) % teachers.length);
  };

  return (
    <section id="teachers" className="py-20 bg-slate-800/30">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div 
          ref={headerRef}
          className={`text-center mb-16 transition-all duration-500 will-change-transform ${
            headerVisible ? 'opacity-100 translate-y-0' : 'opacity-100 translate-y-2'
          }`}
        >
          <Badge className="mb-4 bg-gradient-to-r from-cyan-400/10 to-purple-400/10 border border-cyan-400/20 text-cyan-400">
            Automação de Estratégias
          </Badge>
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
            Melhor Equipe de <span className="text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 to-green-400">Automação de Estratégias</span> do Mercado
          </h2>
          <p className="text-xl text-slate-300 max-w-3xl mx-auto">
            Mais de 20 anos de experiência combinada. Nossa equipe desenvolve e monitora cada algoritmo, 
            garantindo performance e confiabilidade excepcionais.
          </p>
          
          {/* Stats de credibilidade */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 mt-8 max-w-4xl mx-auto">
            <div className="text-center p-4 bg-slate-800/30 rounded-xl border border-slate-700">
              <div className="text-2xl font-bold text-cyan-400">20+</div>
              <div className="text-sm text-slate-400">Anos de Experiência</div>
            </div>
            <div className="text-center p-4 bg-slate-800/30 rounded-xl border border-slate-700">
              <div className="text-2xl font-bold text-green-400">7+</div>
              <div className="text-sm text-slate-400">Dígitos Faturados</div>
            </div>
            <div className="text-center p-4 bg-slate-800/30 rounded-xl border border-slate-700">
              <div className="text-2xl font-bold text-yellow-400">1000+</div>
              <div className="text-sm text-slate-400">Pessoas Ajudadas</div>
            </div>
            <div className="text-center p-4 bg-slate-800/30 rounded-xl border border-slate-700">
              <div className="text-2xl font-bold text-purple-400">Oficial</div>
              <div className="text-sm text-slate-400">Parceiro da Maior Plataforma</div>
            </div>
          </div>
        </div>

        {/* Teachers Carousel */}
        <div className="mb-12">
          <div className="flex justify-center mb-8">
            <div className="flex space-x-4 bg-slate-800/50 p-2 rounded-full">
              {teachers.map((teacher, index) => (
                <button
                  key={teacher.id}
                  onClick={() => setSelectedTeacher(index)}
                  aria-label={`Selecionar especialista ${teacher.name}`}
                  className={`relative overflow-hidden rounded-full transition-all duration-300 focus:outline-none focus:ring-2 focus:ring-cyan-400 focus:ring-offset-2 focus:ring-offset-slate-900 ${
                    selectedTeacher === index 
                      ? 'ring-4 ring-cyan-400 scale-110' 
                      : 'hover:scale-105 opacity-70 hover:opacity-100'
                  }`}
                >
                  <img
                    src={teacher.image}
                    alt={`${teacher.name} - ${teacher.specialty} - Especialista em robô trader automatizado e copy trade QuantMake.AI, desenvolvedor de algoritmos quantitativos com ${teacher.experience} experiência no mercado financeiro brasileiro`}
                    className="w-16 h-16 object-cover"
                    title={`${teacher.name} - Expert em AutoTrade, Day Trade e sistemas de trading automatizado QuantMake`}
                  />
                </button>
              ))}
            </div>
          </div>

          {/* Navigation Arrows */}
          <div className="flex justify-center space-x-4 mb-8">
            <Button
              variant="outline"
              size="sm"
              onClick={prevTeacher}
              aria-label="Especialista anterior"
              className="bg-slate-800/50 border-slate-600 text-white hover:bg-slate-700 rounded-full focus:ring-2 focus:ring-cyan-400 focus:ring-offset-2 focus:ring-offset-slate-900"
            >
              <ChevronLeft className="h-5 w-5" />
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={nextTeacher}
              aria-label="Próximo especialista"
              className="bg-slate-800/50 border-slate-600 text-white hover:bg-slate-700 rounded-full focus:ring-2 focus:ring-cyan-400 focus:ring-offset-2 focus:ring-offset-slate-900"
            >
              <ChevronRight className="h-5 w-5" />
            </Button>
          </div>
        </div>

        {/* Selected Teacher Details */}
        <Card className="bg-slate-800/50 border-slate-700 max-w-4xl mx-auto">
          <CardContent className="p-8">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-center">
              {/* Teacher Image and Basic Info */}
              <div className="text-center lg:text-left">
                <div className="relative inline-block mb-6">
                  <img
                    src={teachers[selectedTeacher].image}
                    alt={`${teachers[selectedTeacher].name} - ${teachers[selectedTeacher].specialty} - Líder em desenvolvimento de robôs trader automáticos QuantMake.AI, especialista em AutoTrade, Day Trade e Copy Trade com ${teachers[selectedTeacher].experience} experiência comprovada no mercado financeiro brasileiro, desenvolvedor de algoritmos para Nelogica Profit e BlackArrow`}
                    className="w-48 h-48 object-cover rounded-2xl mx-auto lg:mx-0"
                    title={`${teachers[selectedTeacher].name} - Expert em trading quantitativo, sistemas automatizados e robôs traders QuantMake.AI`}
                  />
                  <div className="absolute -bottom-4 -right-4 bg-gradient-to-r from-cyan-400 to-green-400 rounded-full p-3">
                    <span className="text-slate-900 font-bold text-sm">
                      {teachers[selectedTeacher].experience}
                    </span>
                  </div>
                </div>
                
                <h3 className="text-2xl font-bold text-white mb-2">
                  {teachers[selectedTeacher].name}
                </h3>
                
                <Badge className="bg-cyan-400/20 text-cyan-400 hover:bg-cyan-400/30 mb-4">
                  {teachers[selectedTeacher].specialty}
                </Badge>
                
                <p className="text-slate-300 leading-relaxed">
                  {teachers[selectedTeacher].focus}
                </p>
              </div>

              {/* Teacher Bio and Achievements */}
              <div>
                <h3 className="text-xl font-bold text-white mb-4">Sobre o Especialista</h3>
                <p className="text-slate-300 leading-relaxed mb-6">
                  {teachers[selectedTeacher].bio}
                </p>

                <h4 className="text-lg font-bold text-white mb-4">Principais Conquistas</h4>
                <div className="space-y-2">
                  {teachers[selectedTeacher].achievements.map((achievement, index) => (
                    <div key={index} className="flex items-center space-x-2">
                      <div className="w-2 h-2 bg-green-400 rounded-full flex-shrink-0"></div>
                      <span className="text-slate-300">{achievement}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Call to Action */}
        <div className="text-center mt-12">
          <div className="bg-gradient-to-r from-slate-800/50 to-slate-700/50 border border-slate-600 rounded-2xl p-8">
            <h3 className="text-2xl font-bold text-white mb-4">
              Confiança Comprovada em <span className="text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 to-green-400">Cada Algoritmo</span>
            </h3>
            <p className="text-slate-300 mb-6 max-w-2xl mx-auto">
              Cada estratégia é desenvolvida, testada e monitorada por nossa equipe de especialistas. 
              Você investe com a segurança de quem tem décadas de experiência no mercado.
            </p>
            <Button 
              size="lg"
              className="bg-gradient-to-r from-cyan-400 to-green-400 hover:from-cyan-500 hover:to-green-500 text-slate-900 font-bold px-8 py-3 rounded-full focus:ring-2 focus:ring-cyan-400 focus:ring-offset-2 focus:ring-offset-slate-800"
              onClick={() => document.getElementById('products')?.scrollIntoView({ behavior: 'smooth' })}
            >
              Ver Nossos Produtos 🚀
            </Button>
          </div>
        </div>
      </div>
    </section>
  );
});

TeachersSection.displayName = 'TeachersSection';

export default TeachersSection;
